import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import axios from 'axios';
import {
  Truck, Wrench, MapPin, ClipboardList, Receipt, DollarSign, Crown,
  Check, ArrowRight, Eye, EyeOff, Building2, User, Mail, Phone, Lock, Tag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_ICONS = {
  chauffeur: Truck,
  entretien: Wrench,
  atelier: Building2,
  localisation: MapPin,
  repartition: ClipboardList,
  facturation: Receipt,
  depenses: DollarSign,
  pro: Crown,
};

const PLAN_COLORS = {
  chauffeur: 'border-slate-600',
  entretien: 'border-amber-500',
  atelier: 'border-blue-500',
  localisation: 'border-emerald-500',
  repartition: 'border-purple-500',
  facturation: 'border-pink-500',
  depenses: 'border-orange-500',
  pro: 'border-amber-400',
};

const PLAN_ACCENT = {
  chauffeur: 'text-slate-400',
  entretien: 'text-amber-500',
  atelier: 'text-blue-500',
  localisation: 'text-emerald-500',
  repartition: 'text-purple-500',
  facturation: 'text-pink-500',
  depenses: 'text-orange-500',
  pro: 'text-amber-400',
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAdminAuth();

  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(searchParams.get('plan') || 'entretien');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    phone: '',
    password: '',
    promo_code: '',
  });

  const [promoResult, setPromoResult] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/saas/plans`)
      .then(res => setPlans(res.data.filter(p => !p.is_free && !p.is_addon)))
      .catch(() => {});
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validatePromo = async () => {
    if (!form.promo_code.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/saas/promo/validate`, {
        code: form.promo_code, email: form.email,
      });
      setPromoResult(res.data);
    } catch (err) {
      setPromoResult(null);
      setError(err.response?.data?.detail || 'Code promo invalide');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/api/saas/register`, {
        ...form,
        plan: selectedPlan,
        billing_cycle: billingCycle,
      });

      // Auto-login with returned token
      localStorage.setItem('admin_token', res.data.access_token);
      window.location.href = '/admin/fleet';
    } catch (err) {
      const msg = err.response?.data?.detail;
      setError(msg || 'Erreur de connexion au serveur. Verifiez votre connexion internet et reessayez.');
    }
    setLoading(false);
  };

  const currentPlan = plans.find(p => p.slug === selectedPlan);
  const price = currentPlan
    ? (billingCycle === 'yearly' ? currentPlan.price_yearly : currentPlan.price_monthly)
    : 0;
  const monthlyEquiv = billingCycle === 'yearly' && currentPlan
    ? (currentPlan.price_yearly / 12).toFixed(2)
    : null;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/admin/login')}>
            <div className="w-9 h-9 rounded-lg overflow-hidden flex items-center justify-center bg-slate-900 ring-1 ring-amber-500/30">
              <img src="/icons/icon-192.png" alt="ProPilote" className="w-full h-full object-cover" />
            </div>
            <span className="text-xl font-bold">ProPilote</span>
          </div>
          <button
            onClick={() => navigate('/admin/login')}
            className="text-sm text-slate-400 hover:text-amber-500 transition-colors"
            data-testid="login-link"
          >
            Deja un compte ? Se connecter
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Commencez votre essai gratuit</h1>
          <p className="text-slate-400">30 jours gratuits, aucune carte de credit requise</p>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-amber-500' : 'text-slate-600'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-500'}`}>1</div>
            <span className="text-sm font-medium hidden sm:inline">Choisir un plan</span>
          </div>
          <div className={`w-12 h-px ${step >= 2 ? 'bg-amber-500' : 'bg-slate-700'}`} />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-amber-500' : 'text-slate-600'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-500'}`}>2</div>
            <span className="text-sm font-medium hidden sm:inline">Creer votre compte</span>
          </div>
        </div>

        {step === 1 && (
          <>
            {/* Billing toggle */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className={`text-sm ${billingCycle === 'monthly' ? 'text-white' : 'text-slate-500'}`}>Mensuel</span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                className={`w-12 h-6 rounded-full relative transition-colors ${billingCycle === 'yearly' ? 'bg-amber-500' : 'bg-slate-700'}`}
                data-testid="billing-toggle"
              >
                <div className={`w-5 h-5 rounded-full bg-white absolute top-0.5 transition-transform ${billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
              <span className={`text-sm ${billingCycle === 'yearly' ? 'text-white' : 'text-slate-500'}`}>
                Annuel <span className="text-green-400 font-semibold">-20%</span>
              </span>
            </div>

            {/* Plans grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="plans-grid">
              {plans.map(plan => {
                const Icon = PLAN_ICONS[plan.slug] || Truck;
                const isSelected = selectedPlan === plan.slug;
                const planPrice = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
                const monthlyEq = billingCycle === 'yearly' ? (plan.price_yearly / 12).toFixed(2) : null;

                return (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.slug)}
                    className={`relative cursor-pointer rounded-xl border-2 p-5 transition-all ${
                      isSelected
                        ? `${PLAN_COLORS[plan.slug]} bg-slate-800/80 shadow-lg scale-[1.02]`
                        : 'border-slate-800 bg-slate-900/50 hover:border-slate-700'
                    }`}
                    data-testid={`plan-card-${plan.slug}`}
                  >
                    {isSelected && (
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                        <Check size={14} className="text-black" />
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-3">
                      <Icon size={20} className={PLAN_ACCENT[plan.slug]} />
                      <h3 className="font-bold text-lg">{plan.name}</h3>
                    </div>
                    <div className="mb-3">
                      <span className="text-2xl font-bold">{planPrice}$</span>
                      <span className="text-slate-500 text-sm">/{billingCycle === 'yearly' ? 'an' : 'mois'}</span>
                      {monthlyEq && (
                        <p className="text-xs text-green-400">{monthlyEq}$/mois</p>
                      )}
                    </div>
                    <p className="text-slate-400 text-xs mb-3">{plan.description}</p>
                    <div className="text-xs text-slate-500">
                      {plan.vehicles_included > 0 && (
                        <p>{plan.vehicles_included} vehicules inclus (+{plan.extra_vehicle_price}$/veh.)</p>
                      )}
                      <p>{plan.trial_days} jours d'essai gratuit</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center">
              <button
                onClick={() => setStep(2)}
                className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-8 py-3 rounded-lg flex items-center gap-2 transition-colors"
                data-testid="next-step-btn"
              >
                Continuer <ArrowRight size={18} />
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <div className="max-w-lg mx-auto">
            {/* Selected plan summary */}
            {currentPlan && (
              <div className={`rounded-xl border-2 ${PLAN_COLORS[selectedPlan]} bg-slate-800/50 p-4 mb-6`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {(() => { const Icon = PLAN_ICONS[selectedPlan] || Truck; return <Icon size={18} className={PLAN_ACCENT[selectedPlan]} />; })()}
                    <span className="font-bold">{currentPlan.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold">{price}$</span>
                    <span className="text-slate-500 text-sm">/{billingCycle === 'yearly' ? 'an' : 'mois'}</span>
                    {monthlyEquiv && <p className="text-xs text-green-400">{monthlyEquiv}$/mois</p>}
                  </div>
                </div>
                <button onClick={() => setStep(1)} className="text-xs text-amber-500 hover:underline mt-2">
                  Changer de plan
                </button>
              </div>
            )}

            {/* Registration form */}
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Prenom *</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-3 text-slate-500" />
                    <input
                      type="text"
                      value={form.first_name}
                      onChange={e => handleChange('first_name', e.target.value)}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:border-amber-500 focus:outline-none"
                      required
                      data-testid="register-first-name"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Nom *</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-3 text-slate-500" />
                    <input
                      type="text"
                      value={form.last_name}
                      onChange={e => handleChange('last_name', e.target.value)}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:border-amber-500 focus:outline-none"
                      required
                      data-testid="register-last-name"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-400 mb-1 block">Nom de l'entreprise *</label>
                <div className="relative">
                  <Building2 size={16} className="absolute left-3 top-3 text-slate-500" />
                  <input
                    type="text"
                    value={form.company_name}
                    onChange={e => handleChange('company_name', e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:border-amber-500 focus:outline-none"
                    required
                    data-testid="register-company"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-400 mb-1 block">Courriel *</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-3 text-slate-500" />
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => handleChange('email', e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:border-amber-500 focus:outline-none"
                    required
                    data-testid="register-email"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-400 mb-1 block">Telephone</label>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-3 text-slate-500" />
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => handleChange('phone', e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:border-amber-500 focus:outline-none"
                    data-testid="register-phone"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-400 mb-1 block">Mot de passe *</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-3 text-slate-500" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => handleChange('password', e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-10 py-2.5 text-sm focus:border-amber-500 focus:outline-none"
                    minLength={6}
                    required
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-400 mb-1 block">Code promo (optionnel)</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Tag size={16} className="absolute left-3 top-3 text-slate-500" />
                    <input
                      type="text"
                      value={form.promo_code}
                      onChange={e => { handleChange('promo_code', e.target.value.toUpperCase()); setPromoResult(null); }}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:border-amber-500 focus:outline-none uppercase"
                      placeholder="VOTRE-CODE"
                      data-testid="register-promo"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={validatePromo}
                    className="px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm hover:bg-slate-700 transition-colors"
                    data-testid="validate-promo-btn"
                  >
                    Valider
                  </button>
                </div>
                {promoResult && (
                  <p className="text-green-400 text-xs mt-1 flex items-center gap-1">
                    <Check size={12} /> {promoResult.description}
                  </p>
                )}
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm" data-testid="register-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold py-3 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                ) : (
                  <>Commencer l'essai gratuit de 30 jours</>
                )}
              </button>

              <p className="text-center text-xs text-slate-500">
                En vous inscrivant, vous acceptez nos conditions d'utilisation.
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterPage;
